import React, { useState, useRef } from 'react';
import { CalendarIcon } from 'lucide-react';

interface SingleDatePickerProps {
  value: string;
  onChange: (date: string) => void;
  label?: string;
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const getToday = () => {
  return new Date().toISOString().split('T')[0];
};

const SingleDatePicker: React.FC<SingleDatePickerProps> = ({
  value,
  onChange,
  label,
  minDate,
  maxDate,
  placeholder = 'Select date',
  disabled = false,
  className = ''
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [tempValue, setTempValue] = useState(value || getToday());
  const pickerRef = useRef<HTMLDivElement>(null);

  // Update tempValue when value changes
  React.useEffect(() => {
    if (value) {
      setTempValue(value);
    } else {
      setTempValue(getToday());
    }
  }, [value]);

  // Initialize tempValue to today when picker opens if no value
  React.useEffect(() => {
    if (showPicker && !value) {
      setTempValue(getToday());
    }
  }, [showPicker, value]);

  // Close picker when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getMaxDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 365); // 1 year from now
    return date.toISOString().split('T')[0];
  };

  const handleDateSelect = (dateString: string) => {
    setTempValue(dateString);
    onChange(dateString);
    setShowPicker(false);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const currentValue = tempValue || getToday();
    const parts = currentValue.split('-');
    const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    if (direction === 'prev') {
      date.setMonth(date.getMonth() - 1);
    } else {
      date.setMonth(date.getMonth() + 1);
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    setTempValue(`${year}-${month}-${day}`);
  };

  const generateCalendarDays = () => {
    const currentValue = tempValue || getToday();
    const parts = currentValue.split('-');
    const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="p-2" />);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isSelected = dateString === value;
      const isToday = dateString === getToday();
      const isDisabled = (minDate && new Date(dateString) < new Date(minDate)) || (maxDate && new Date(dateString) > new Date(maxDate));
      
      days.push(
        <button
          key={day}
          type="button"
          onClick={() => !isDisabled && handleDateSelect(dateString)}
          disabled={isDisabled || false}
          className={`
            p-2 text-sm rounded-md transition-colors
            ${isToday ? 'bg-blue-100 text-blue-800 font-semibold' : ''}
            ${isSelected ? 'bg-blue-600 text-white' : ''}
            ${!isDisabled && !isSelected ? 'hover:bg-gray-100' : ''}
            ${isDisabled ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          {day}
        </button>
      );
    }
    
    return days;
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setShowPicker(!showPicker)}
          disabled={disabled}
          className={`
            w-full px-3 py-2 text-left border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500
            ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white text-gray-900'}
            ${!disabled ? 'hover:border-blue-500' : ''}
          `}
        >
          <div className="flex items-center justify-between">
            <span className={value ? '' : 'text-gray-500'}>
              {value ? formatDate(value) : placeholder}
            </span>
            <CalendarIcon className="h-4 w-4 text-gray-400" />
          </div>
        </button>
        
        {showPicker && (
          <div
            ref={pickerRef}
            className="absolute z-50 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 p-4"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={() => navigateMonth('prev')}
                className="p-1 rounded-md hover:bg-gray-100 text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <div className="text-sm font-medium text-gray-900">
                {(() => {
                  const currentValue = tempValue || getToday();
                  const parts = currentValue.split('-');
                  const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                  return date.toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric'
                  });
                })()}
              </div>
              
              <button
                type="button"
                onClick={() => navigateMonth('next')}
                className="p-1 rounded-md hover:bg-gray-100 text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-medium text-gray-500 p-2">
                  {day}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-1">
              {generateCalendarDays()}
            </div>
            
            {/* Footer */}
            <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={() => setShowPicker(false)}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SingleDatePicker;
